import { DatePicker, LocalizationProvider, type DatePickerProps } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useState, type JSX } from "react";

import { areBoundFieldPropsEqual } from "./MemoHelper";

import type { TextFieldProps } from "@mui/material";
import type { BoundedValue, ErrorMap } from "@repo/shared/common";
import type { Dayjs } from "dayjs";

type BoundDateFieldProps = {
	value: string; // ISO string "YYYY-MM-DD" oder ""
	onChangeValue: (value: string) => void;
	label: string;
	locale: string;
	format: string;

	errors?: ErrorMap;
	errorKey?: string;

	readOnly?: boolean;
	additionalProps?: Omit<
		DatePickerProps,
		"label" | "value" | "format" | "onChange" | "readOnly" | "slotProps"
	>;
	textFieldProps?: Omit<TextFieldProps, "value" | "onChange" | "label" | "error" | "helperText">;
};

function PureBoundDateField({
	value,
	onChangeValue,
	label,
	locale,
	format,
	errors,
	errorKey,
	readOnly,
	additionalProps,
	textFieldProps,
}: BoundDateFieldProps): JSX.Element {
	const key = errorKey;
	const errorMessage = key != null ? errors?.[key] : undefined;

	// "" → null, damit DatePicker leeres Feld anzeigt
	const dayjsValue: Dayjs | null =
		value && value !== "Invalid Date" ? dayjs(value, "YYYY-MM-DD", true) : null;

	const handleChange = (newValue: Dayjs | null): void => {
		if (newValue == null) {
			onChangeValue("");
		} else {
			onChangeValue(newValue.format("YYYY-MM-DD"));
		}
	};

	const [open, setOpen] = useState<boolean>(false);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
			<DatePicker
				open={open}
				onClose={
					readOnly === true
						? undefined
						: () => {
								setOpen(false);
							}
				}
				onOpen={
					readOnly === true
						? undefined
						: () => {
								setOpen(true);
							}
				}
				label={label}
				value={dayjsValue}
				format={format}
				onChange={handleChange}
				readOnly={readOnly}
				slotProps={{
					textField: {
						helperText: errorMessage,
						error: Boolean(errorMessage),
						onClick: () => {
							setOpen(!open);
						},
						...textFieldProps,
					},
				}}
				{...additionalProps}
			/>
		</LocalizationProvider>
	);
}

const MemoPureBoundDateField = React.memo(PureBoundDateField, areBoundFieldPropsEqual);

export function BoundDateField(
	props: {
		boundedValue: BoundedValue<string | null>;
	} & Omit<BoundDateFieldProps, "value" | "onChangeValue" | "errorKey" | "locale" | "format">,
): JSX.Element {
	const { i18n } = useTranslation();

	const locale = i18n.language ?? "en";
	const format = locale === "de" ? "DD.MM.YYYY" : "MM/DD/YYYY";

	return (
		<MemoPureBoundDateField
			{...props}
			locale={locale}
			format={format}
			value={props.boundedValue.value ?? ""}
			onChangeValue={(v) => {
				props.boundedValue.onChangeValue(v);
			}}
			errorKey={props.boundedValue.errorKey}
			errors={props.boundedValue.errorMap}
		/>
	);
}
